import { autoinject } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { Toast, Toaster } from 'app/shared/lib/toaster';
import { deepClone, getMinMax } from 'app/shared/lib/utilities';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { DateValue } from 'app/shared/lib/date-time';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveCareDay extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object protected careDays: UM.AdmissionCareDay[] = [];
    @bindable.boolean protected showActions: boolean = false;

    private firstCareDay: UM.AdmissionCareDay = {};
    protected statusReasons: UM.AdmissionCareDayStatusReason[] = [];
    protected selectedStatusReasons: UM.AdmissionCareDayStatusReason[] = [];

    protected statuses: UM.AdmissionCareDayStatus[] = [];
    protected levelsOfCare: UM.LevelOfCare[] = [];
    protected auth: UM.Auth | null = null;

    protected startDate: string | null = null;
    protected endDate: string | null = null;
    protected choice: "single" | "multiple" | null = "single";
    protected id: guid = guid();

    get isMultiDay(): boolean {
        return this.choice === 'multiple'
    }

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly toaster: Toaster,
        protected readonly service: AdmissionCareDayService,
        private readonly context: AuthPageContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(bindingContext: Object): void {
        const authContext = bindingContext as AuthPageContext;
        this.auth = authContext.auth;
        this.loadLookups(null);
        this.firstCareDay.dateReceived = this.env.now();
    }

    ////****************** Events ***************************************************////
    protected async statusChanged(newValue: any): Promise<void> {
        this.statusReasons = [];
        this.selectedStatusReasons = [];

        if(!this.firstCareDay || !this.firstCareDay.status || !this.firstCareDay.status.id) {
            if(this.firstCareDay?.levelOfCare)  this.firstCareDay.levelOfCare.approved = null;
            return;
        }

        try {
            this.state.beginLoad();

            const response = await this.service.loadStatusReasons(this.firstCareDay.status.id);
            this.statusReasons = response.results;

            if(this.firstCareDay.status.authStatus && this.firstCareDay.status.authStatus.flag && this.firstCareDay.status.authStatus.flag.isApproval &&
                this.firstCareDay.levelOfCare && !this.firstCareDay.levelOfCare.approved) {
                this.firstCareDay.levelOfCare.approved = this.firstCareDay.levelOfCare.actual;
            } else if(this.firstCareDay.levelOfCare?.approved){
                this.firstCareDay.levelOfCare.approved = null;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected async careDaysChanged(newValue: UM.AdmissionCareDay): Promise<void> {
        const firstCareDay = this.careDays[0] || {};
        await this.loadLookups(firstCareDay);
        this.firstCareDay = firstCareDay
        if(this.firstCareDay.status) {
            this.selectedStatusReasons = this.firstCareDay.status?.reasons || [];
        }

        if(!this.firstCareDay.id) {
            this.firstCareDay.dateReceived = this.env.now();
        }
        if(!firstCareDay.auth && this.auth) {
            this.careDays.forEach((careDay) => {
                careDay.auth = this.auth;
            });
        }

        let dateReceived = firstCareDay.metadata
            ? firstCareDay.metadata && firstCareDay.metadata.created && firstCareDay.metadata.created.timestamp
            : this.env.now();

        this.careDays.forEach((careDay) => {
            careDay.dateReceived = dateReceived;
        });

        if(this.careDays.length > 1) {
            this.choice = 'multiple';
        }

        if(firstCareDay.careDate) {
            this.startDate = firstCareDay.careDate;

            if(this.careDays.length > 0) {
                this.endDate = this.careDays[this.careDays.length - 1].careDate || this.startDate;
            }

        } else {
            firstCareDay.careDate = this.startDate = this.endDate = this.calculateNextCareDay();
        }
    }

    ////****************** Functions ************************************************////
    protected calculateNextCareDay(): string {
        if(!this.auth || !this.auth.admission || !this.auth.admission.careDays) {
            return this.env.today;
        }

        const dates: string[] = [];
        this.auth.admission.careDays.forEach((day) => {
            if(day.careDate) {
                dates.push(day.careDate);
            }
        });

        const [, max] = getMinMax(...dates);
        return (new DateValue(max)).add(1, 'days').toString();
    }

    protected getSaveDates(): string[] {
        const start: DateValue = new DateValue(this.startDate!);
        const end: DateValue = this.isMultiDay ? new DateValue(this.endDate!) : start;
        let dates: string[] = [start.toString()];

        if(!end.value || start.isEqual(end)) {
            return dates;
        }

        let current: DateValue = start;

        while(!current.isEqual(end)) {
            current = current.add(1, 'days');
            dates.push(current.toString());

            if(dates.length > 1000) {
                throw new Error('Care Day Range too large');
            }
        }

        return dates;
    }

    protected validateDates(): boolean {
        if(this.firstCareDay && this.firstCareDay.id) return true;
        if(!this.auth || !this.auth.admission) return false;
        if(!this.auth.admission.careDays) return true;

        let isValid = true;
        const saveDates = this.getSaveDates();

        this.auth.admission.careDays.forEach(careDay => {
            saveDates.forEach(date => {
                if(careDay.careDate == date) isValid = false;
            });
        });

        return isValid;
    }

    protected constructSaveObject(): UM.AdmissionCareDay[] {
        let results: UM.AdmissionCareDay[] = [];
        let daysToSave: UM.AdmissionCareDay[] = [];

        if(this.firstCareDay && this.firstCareDay.id) {
            daysToSave = this.careDays;
        } else {
            let dates: string[] = this.getSaveDates();
            dates.forEach((date) => {
                let temp: UM.AdmissionCareDay = {
                    id: null,
                    careDate: date
                }

                daysToSave.push(temp);
            });
        }

        daysToSave.forEach((careDay) => {
            if(!this.firstCareDay) return;

            let temp: UM.AdmissionCareDay = {
                id: careDay.id,
                auth: getReference(this.firstCareDay.auth || this.context.auth),
                careDate: careDay.careDate,
                dateReceived: this.firstCareDay.dateReceived,
                levelOfCare: {
                    approved: getReference(this.firstCareDay.levelOfCare && this.firstCareDay.levelOfCare.approved),
                    actual: getReference(this.firstCareDay.levelOfCare && this.firstCareDay.levelOfCare.actual)
                },
                status: getReference(this.firstCareDay.status),
            }

            if(temp.status) {
                temp.status.reasons = getReferences(this.selectedStatusReasons);
            }

            results.push(temp);
        });

        return results;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveCareDays();
    }

    public reset(): void {
        this.careDays = [];
        this.startDate = null;
        this.endDate = null;
        this.choice = "single";
        this.firstCareDay = {};
        this.selectedStatusReasons = [];
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadLookups(firstCareDay: UM.AdmissionCareDay | null): Promise<void> {
        try {
            this.state.beginLoad();

            const status = deepClone(firstCareDay && firstCareDay.status || {});
            const hasStatus: boolean = !!(firstCareDay && firstCareDay.status && firstCareDay.status.id);

            const transitionsIncludes = ['transitions', 'transitions.authStatus'];
            const transitionsFields = ['description', 'transitions.authStatus.flag.isApproval', 'transitions.description', 'flag.isInitialStatus'];
            const statusReasonsFields = ['description', 'status.id'];
            const levelsOfCareFields = ['description'];

            const transitionsPromise = hasStatus
                ? this.service.loadStatusTransitions(status.id || '', {}, transitionsIncludes, transitionsFields)
                : this.service.loadInitialStatus(transitionsIncludes, transitionsFields);

            const statusReasonsPromise = hasStatus
                ? this.service.loadStatusReasons(status.id || '', undefined, [], statusReasonsFields)
                : Promise.resolve(null);

            const levelsOfCarePromise = this.service.loadLevelsOfCare(undefined, undefined, levelsOfCareFields);

            const [transitions, statusReasons, levelsOfCare] = await Promise.all([transitionsPromise, statusReasonsPromise, levelsOfCarePromise]);

            const statuses: UM.AdmissionCareDayStatus[] = [];

            if(!hasStatus && transitions?.results[0].flag?.isInitialStatus) {
                statuses.push(transitions.results[0]);
            } else if(transitions?.results[0]?.transitions?.length) {
                if(firstCareDay && firstCareDay.status) {
                    statuses.push(status);
                }
                statuses.push(...transitions.results[0].transitions);
            }

            this.statuses = statuses;

            if(statusReasons) {
                this.statusReasons = statusReasons.results;
            }

            if(levelsOfCare) {
                this.levelsOfCare = levelsOfCare.results;
            }

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveCareDays(): Promise<boolean> {
        const saveCareDays = this.constructSaveObject();

        let isSuccessful = false;
        try {
            this.state.beginSave();
            let progressToast: Toast | undefined;
            if(saveCareDays.length > 1) {
                progressToast = this.toaster.standard.batch('um:auth.careDays.$', saveCareDays.length)
                progressToast.show();
            }

            // Execute all inserts and updates in their own API batch.
            const responses = await this.service.applyCareDayBatch(saveCareDays, progressToast);
            isSuccessful = allActionsSucceeded(responses);
            this.context.triggerCorrespondenceRules();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:auth.careDays.$', isSuccessful, saveCareDays.length);
            this.state.endSave();
            this.reset();
            return isSuccessful;
        }
    }
}
